/**
 * Read-only "Intake Package" status panel at the top of the Documents tab —
 * ported from the cbs-portal demo IntakePackagePanel. Shows what this
 * group/account has on file at a glance; the actionable side is the uploader
 * below (upload a file with the matching category to complete a slot).
 *
 * Status is derived, never fabricated — see @common/intakePackage.
 */

import React from 'react';
import {
  AlertCircle,
  ArrowUpRight,
  CheckCircle2,
  ChevronDown,
  PackageCheck,
  Upload,
} from 'lucide-react';
import {trpc} from '@common-web/lib/trpc';
import {
  achFormHasData,
  deriveIntakePackage,
  intakePackageSlots,
  rfcFormDataHasAch,
  type IntakeSlotStatus,
} from '@common/intakePackage';
import type {RfcAchForm} from '@common/lib/rfcSchema';

interface IntakePackagePanelProps {
  accountsId: string;
  /** Jump to the RFC tab (for slots satisfied by the digital RFC). */
  onNavigateToRfc?: () => void;
  /** Scroll to the file list below (for slots satisfied by an upload). */
  onViewDocuments?: () => void;
  /** Start an upload prefilled with the missing slot's document category
   *  (cbs-portal: missing essentials are clickable). */
  onUploadMissing?: (category: string) => void;
}

export function IntakePackagePanel({
  accountsId,
  onNavigateToRfc,
  onViewDocuments,
  onUploadMissing,
}: IntakePackagePanelProps): React.ReactElement | null {
  const account = trpc.account.getById.useQuery({accountsId});
  const docs = trpc.account.getDocuments.useQuery({accountsId});

  if (!account.data || !docs.data) return null;

  const rfc = account.data.rfcSubmission;
  // Raw form-JSON truthiness would false-positive here: every RFC-creation
  // path seeds blank form JSON, so use the server's stricter rfcProvided
  // (worked-on / submitted / attachment on file) for the RFC slot instead.
  const hasRfcFormData = account.data.rfcProvided;
  const hasAchFormData =
    achFormHasData(rfc?.achFormData as RfcAchForm | null | undefined) ||
    rfcFormDataHasAch(rfc?.section125FormData) ||
    rfcFormDataHasAch(rfc?.mecFormData);

  const slots = intakePackageSlots(account.data.clearSpringFormEnabled);
  // Slot key → document category, for the missing-slot upload shortcut.
  const categoryBySlotKey = new Map(
    slots.filter((s) => s.category).map((s) => [s.key, s.category!]),
  );
  const rows = deriveIntakePackage({
    slots,
    hasRfcFormData,
    hasAchFormData,
    documents: docs.data.map((d) => ({category: d.category, fileName: d.fileName})),
    isLive: account.data.lifecyclePhase === 'ACTIVE',
  });
  // 'received' is inferred for live accounts (passed go-live, no discrete
  // upload here) — real document evidence is only the 'onfile' rows, so the
  // summary reports the two separately instead of lumping them together.
  const onFile = rows.filter((r) => r.state === 'onfile').length;
  const inferredReceived = rows.filter((r) => r.state === 'received').length;
  const missing = rows.length - onFile - inferredReceived;

  const viewHandler = (row: IntakeSlotStatus): (() => void) | undefined => {
    if (row.state !== 'onfile') return undefined;
    if (row.source === 'rfc') return onNavigateToRfc;
    if (row.source === 'documents') return onViewDocuments;
    return undefined;
  };

  return (
    <details open className='group overflow-hidden rounded-lg border border-border bg-white'>
      <summary className='flex cursor-pointer select-none list-none items-center gap-2.5 px-4 py-3 group-open:border-b group-open:border-border [&::-webkit-details-marker]:hidden'>
        <PackageCheck size={16} className='shrink-0 text-primary' />
        <h3 className='text-sm font-semibold text-foreground'>Intake Package</h3>
        <span className='text-xs text-muted-foreground'>what this group has on file</span>
        <span
          className={`ml-auto rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums ${
            missing ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
          }`}
        >
          {onFile}/{rows.length} on file
          {inferredReceived ? ` · ${inferredReceived} received` : ''}
          {missing ? ` · ${missing} missing` : ''}
        </span>
        <ChevronDown
          size={16}
          className='shrink-0 text-muted-foreground transition-transform group-open:rotate-180'
        />
      </summary>

      <div className='grid grid-cols-1 md:grid-cols-2'>
        {rows.map((row, i) => {
          const received = row.state !== 'missing';
          const onView = viewHandler(row);
          return (
            <div
              key={row.key}
              className={`flex items-center gap-3 border-b border-border/50 px-4 py-3 ${
                i % 2 === 0 ? 'md:border-r md:border-r-border/50' : ''
              }`}
            >
              {received ? (
                <CheckCircle2 size={17} className='shrink-0 text-green-600' />
              ) : (
                <AlertCircle size={17} className='shrink-0 text-amber-500' />
              )}
              <div className='min-w-0 flex-1'>
                <div className='flex flex-wrap items-center gap-2'>
                  <span className='text-sm font-semibold text-foreground'>{row.label}</span>
                  {received ? (
                    <span className='rounded bg-green-100 px-1.5 py-0.5 text-xs font-semibold text-green-700'>
                      {row.state === 'onfile' ? 'On file' : 'Received'}
                    </span>
                  ) : (
                    <span className='rounded bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-700'>
                      Not on file
                    </span>
                  )}
                </div>
                <p className='mt-0.5 truncate text-xs text-muted-foreground'>{row.help}</p>
              </div>
              {onView && (
                <button
                  type='button'
                  onClick={onView}
                  className='inline-flex shrink-0 items-center gap-0.5 rounded-md px-2 py-1 text-xs font-semibold text-primary hover:bg-muted'
                >
                  View <ArrowUpRight size={13} />
                </button>
              )}
              {/* Missing slots are actionable: upload prefilled with the
                  slot's category, or jump to the RFC for the RFC slot. */}
              {!received && onUploadMissing && categoryBySlotKey.has(row.key) && (
                <button
                  type='button'
                  onClick={() => onUploadMissing(categoryBySlotKey.get(row.key)!)}
                  className='inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-primary hover:bg-muted'
                >
                  <Upload size={13} /> Upload
                </button>
              )}
              {!received && row.key === 'rfc' && onNavigateToRfc && (
                <button
                  type='button'
                  onClick={onNavigateToRfc}
                  className='inline-flex shrink-0 items-center gap-0.5 rounded-md px-2 py-1 text-xs font-semibold text-primary hover:bg-muted'
                >
                  Fill out <ArrowUpRight size={13} />
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className='bg-muted/30 px-4 py-2.5 text-xs text-muted-foreground'>
        The full document list is below — upload a file with the matching category to complete a
        slot.
      </div>
    </details>
  );
}
